import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

interface UIContextValue {
  zoom: boolean;
  setZoom: React.Dispatch<React.SetStateAction<boolean>>;
  phoneResponsive: boolean;
  openBackdrop: boolean;
  handleToggle: () => void;
  handleCloseBackdrop: () => void;
}

const UIContext = createContext<UIContextValue | null>(null);

export function UIProvider({ children }: { children: ReactNode }) {
  const theme = useTheme();
  const [zoom, setZoom] = useState(true);
  const [openBackdrop, setOpenBackdrop] = useState(false);
  const phoneResponsive = useMediaQuery(theme.breakpoints.down('xs'));

  const handleCloseBackdrop = useCallback(() => {
    setOpenBackdrop(false);
  }, []);

  const handleToggle = useCallback(() => {
    setOpenBackdrop((prev) => !prev);
  }, []);

  const value = useMemo(
    () => ({
      zoom,
      setZoom,
      phoneResponsive,
      openBackdrop,
      handleToggle,
      handleCloseBackdrop,
    }),
    [zoom, phoneResponsive, openBackdrop, handleToggle, handleCloseBackdrop],
  );

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
}

export function useUI(): UIContextValue {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error('useUI must be used within UIProvider');
  }
  return context;
}
